/* Atelier Theme – Watch Atelier Munich */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* ---------- Overlays aus dem Header in den Body verschieben (Stacking-Kontext) ---------- */
  function moveOverlaysToBody() {
    $$('[data-move-to-body]').forEach((el) => {
      if (el.parentElement === document.body) return;
      const existing = el.id && document.body.querySelector(`:scope > #${el.id}`);
      if (existing) existing.remove();
      document.body.appendChild(el);
    });
  }

  /* ---------- Backdrop & Drawer ---------- */
  const backdrop = () => $('[data-backdrop]');
  let activeLayer = null;

  function showBackdrop() {
    const b = backdrop();
    if (!b) return;
    b.hidden = false;
    requestAnimationFrame(() => b.classList.add('is-visible'));
  }
  function hideBackdrop() {
    const b = backdrop();
    if (!b) return;
    b.classList.remove('is-visible');
    setTimeout(() => { if (!activeLayer) b.hidden = true; }, 300);
  }

  function openDrawer(name) {
    const drawer = $(`[data-drawer="${name}"]`);
    if (!drawer) return;
    closeAll();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    activeLayer = drawer;
    document.body.classList.add('no-scroll');
    showBackdrop();
    const focusable = drawer.querySelector('button, a, input');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 50);
  }

  function closeAll() {
    $$('[data-drawer].is-open').forEach((d) => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    closeSearch(false);
    activeLayer = null;
    document.body.classList.remove('no-scroll');
    hideBackdrop();
  }

  /* ---------- Suche ---------- */
  function openSearch() {
    const overlay = $('[data-search]');
    if (!overlay) return;
    closeAll();
    overlay.hidden = false;
    activeLayer = overlay;
    showBackdrop();
    const input = $('[data-predictive-input]', overlay);
    if (input) setTimeout(() => input.focus(), 50);
  }
  function closeSearch(withBackdrop = true) {
    const overlay = $('[data-search]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    if (withBackdrop) {
      activeLayer = null;
      hideBackdrop();
    }
  }

  function initPredictiveSearch() {
    const overlay = $('[data-search]');
    if (!overlay || overlay.dataset.ready) return;
    overlay.dataset.ready = 'true';
    const input = $('[data-predictive-input]', overlay);
    const results = $('[data-predictive-results]', overlay);
    if (!input || !results || !theme.routes.predictiveSearch) return;
    let controller;

    const search = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        overlay.classList.remove('has-results');
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
        'resources[options][unavailable_products]': theme.searchAvailableOnly ? 'hide' : 'last'
      });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        results.innerHTML = content ? content.outerHTML : '';
        overlay.classList.toggle('has-results', Boolean(content));
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    }, 280);

    input.addEventListener('input', search);
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initAnnouncement() {
    const mobile = window.matchMedia('(max-width: 749px)');
    $$('[data-rotator]').forEach((rotator) => {
      if (rotator.dataset.ready) return;
      rotator.dataset.ready = 'true';
      // Handy: Texte mit „ · “ als einzelne Teile (data-view="mobile"), Desktop: ganzer Text (data-view="desktop")
      const current = () => $$('.announcement__msg', rotator).filter((el) => el.dataset.view === 'all' || el.dataset.view === (mobile.matches ? 'mobile' : 'desktop'));
      let i = 0;
      const show = () => {
        const items = current();
        $$('.announcement__msg', rotator).forEach((el) => el.classList.remove('is-active'));
        if (items.length) items[i % items.length].classList.add('is-active');
      };
      show();
      mobile.addEventListener('change', () => { i = 0; show(); });
      setInterval(() => {
        if (current().length < 2) return;
        i += 1;
        show();
      }, parseInt(rotator.dataset.interval, 10) || 5000);
    });
  }

  /* ---------- Hero-Slideshow ---------- */
  function initSlideshows(ctx = document) {
    $$('[data-slideshow]', ctx).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = 'true';
      const slides = $$('[data-slide]', el);
      const dots = $$('[data-slide-to]', el);
      if (slides.length < 2) return;
      let index = 0;
      let timer;

      const go = (n) => {
        slides[index].classList.remove('is-active');
        if (dots[index]) dots[index].classList.remove('is-active');
        index = (n + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        if (dots[index]) dots[index].classList.add('is-active');
      };
      const play = () => {
        const speed = parseInt(el.dataset.autoplay, 10);
        if (!speed) return;
        clearInterval(timer);
        timer = setInterval(() => go(index + 1), speed);
      };

      dots.forEach((dot) => dot.addEventListener('click', () => { go(parseInt(dot.dataset.slideTo, 10)); play(); }));
      const prev = $('[data-slide-prev]', el);
      const next = $('[data-slide-next]', el);
      if (prev) prev.addEventListener('click', () => { go(index - 1); play(); });
      if (next) next.addEventListener('click', () => { go(index + 1); play(); });

      let startX = 0;
      el.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      el.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) { go(index + (dx < 0 ? 1 : -1)); play(); }
      });

      el.addEventListener('shopify:block:select', (e) => {
        const i = slides.indexOf(e.target);
        if (i > -1) { clearInterval(timer); go(i); }
      });
      play();
    });
  }

  /* ---------- Scroll-Slider ---------- */
  function initSliders(ctx = document) {
    $$('[data-slider]', ctx).forEach((slider) => {
      if (slider.dataset.ready) return;
      slider.dataset.ready = 'true';
      const track = $('[data-slider-track]', slider);
      const prev = $('[data-slider-prev]', slider);
      const next = $('[data-slider-next]', slider);
      if (!track) return;

      const step = () => {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        const nav = prev && prev.parentElement;
        if (nav) nav.hidden = track.scrollWidth <= track.clientWidth + 2;
      };
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step() }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------- Produktgalerie ---------- */
  function initGallery(ctx = document) {
    $$('[data-gallery]', ctx).forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = $('[data-gallery-track]', gallery);
      const thumbs = $$('[data-gallery-thumb]', gallery);
      const counter = $('[data-gallery-current]', gallery);
      if (!track) return;

      const prev = $('[data-gallery-prev]', gallery);
      const next = $('[data-gallery-next]', gallery);
      const count = $$('[data-gallery-slide]', gallery).length;
      const setActive = (i) => {
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
        if (counter) counter.textContent = i + 1;
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= count - 1;
        const thumb = thumbs[i];
        if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      };
      const current = () => Math.round(track.scrollLeft / track.clientWidth);
      const goTo = (i) => {
        const target = Math.max(0, Math.min(count - 1, i));
        track.scrollTo({ left: track.clientWidth * target, behavior: 'smooth' });
        setActive(target);
      };
      if (prev) prev.addEventListener('click', () => goTo(current() - 1));
      if (next) next.addEventListener('click', () => goTo(current() + 1));
      // Pfeiltasten, solange der Fokus in der Galerie liegt
      gallery.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const i = parseInt(thumb.dataset.galleryThumb, 10);
          track.scrollTo({ left: track.clientWidth * i });
          setActive(i);
        });
      });

      track.addEventListener('scroll', debounce(() => {
        setActive(Math.round(track.scrollLeft / track.clientWidth));
      }, 80), { passive: true });

      const section = gallery.closest('[data-product-section]');
      const lightbox = section && $('[data-lightbox]', section);
      if (lightbox && typeof lightbox.showModal === 'function') {
        const img = $('[data-lightbox-img]', lightbox);
        $$('[data-zoom]', gallery).forEach((btn) => {
          btn.addEventListener('click', () => {
            img.src = btn.dataset.zoom;
            img.alt = (btn.querySelector('img') || {}).alt || '';
            lightbox.showModal();
          });
        });
        lightbox.addEventListener('click', () => lightbox.close());
      }
    });
  }

  /* ---------- Varianten ---------- */
  // Bei jedem Wechsel rendert Shopify Preis, Verfügbarkeit und Kaufbereich für die gewählte Variante neu
  // (Section Rendering API). So gelten „Preis auf Anfrage“, 0 €, Vergleichspreis und Ausverkauft immer passend.
  function initVariants(ctx = document) {
    $$('[data-variant-select]', ctx).forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      const section = select.closest('[data-product-section]');
      select.addEventListener('change', async () => {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', select.value);
        window.history.replaceState({}, '', url.toString());
        const buttons = $$('[data-product-actions] button', section);
        buttons.forEach((b) => { b.disabled = true; });
        try {
          const res = await fetch(`${window.location.pathname}?variant=${select.value}&section_id=${section.dataset.sectionId}`);
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          ['[data-product-price]', '[data-product-actions]'].forEach((sel) => {
            const fresh = $(sel, doc);
            const old = $(sel, section);
            if (fresh && old) old.replaceWith(fresh);
          });
          const fresh = $('[data-product-section]', doc);
          const mediaId = fresh && fresh.dataset.variantMedia;
          const slide = mediaId && $(`[data-media-id="${mediaId}"]`, section);
          const thumb = slide && $(`[data-gallery-thumb="${slide.dataset.gallerySlide}"]`, section);
          if (thumb) thumb.click();
        } catch (err) {
          // Ohne Antwort lieber neu laden als einen falschen Preis oder Kaufbutton stehen lassen
          window.location.href = url.toString();
        }
      });
    });
  }

  /* ---------- Warenkorb ---------- */
  function toast(message) {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  }

  function renderCartDrawer(sections) {
    if (!sections || !sections['cart-drawer']) return;
    const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = doc.querySelector('#CartDrawerInner');
    const current = $('#CartDrawerInner');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }

  async function refreshCount() {
    try {
      const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      updateCartCount(cart.item_count);
    } catch (e) { /* ignore */ }
  }

  function initProductForms(ctx = document) {
    $$('form[data-type="add-to-cart-form"]', ctx).forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      form.addEventListener('submit', async (e) => {
        if (theme.cartType !== 'drawer' || !$('#CartDrawer')) return;
        e.preventDefault();
        const btn = $('[data-add-to-cart]', form);
        const error = $('[data-form-error]', form);
        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
        if (error) error.hidden = true;

        const data = new FormData(form);
        data.append('sections', 'cart-drawer');
        data.append('sections_url', window.location.pathname);

        try {
          const res = await fetch(`${theme.routes.cartAdd}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: data
          });
          const json = await res.json();
          if (!res.ok || json.status) {
            if (error) { error.textContent = json.description || json.message || theme.strings.error; error.hidden = false; }
            return;
          }
          renderCartDrawer(json.sections);
          await refreshCount();
          openDrawer('cart');
        } catch (err) {
          if (error) { error.textContent = theme.strings.error; error.hidden = false; }
        } finally {
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        }
      });
    });
  }

  async function changeCartLine(line, quantity) {
    const drawer = $('#CartDrawer');
    if (drawer) drawer.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
      });
      const json = await res.json();
      if (!res.ok || json.status) {
        toast(json.description || json.message || theme.strings.error);
        return;
      }
      renderCartDrawer(json.sections);
      updateCartCount(json.item_count);
    } catch (e) {
      toast(theme.strings.error);
    } finally {
      if (drawer) drawer.classList.remove('is-loading');
    }
  }

  /* ---------- Filter & Sortierung ---------- */
  function initFacets(ctx = document) {
    const form = $('[data-facet-form]', ctx);
    const sort = $('[data-sort-select]', ctx);
    const desktop = window.matchMedia('(min-width: 990px)');

    const submit = () => {
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      window.location.href = `${form.action.split('?')[0]}?${params.toString()}`;
    };

    if (form && !form.dataset.ready) {
      form.dataset.ready = 'true';
      form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
      form.addEventListener('change', (e) => {
        if (!desktop.matches) return;
        if (e.target.type === 'number') return;
        submit();
      });
      $$('input[type="number"]', form).forEach((input) => {
        input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
        input.addEventListener('change', () => { if (desktop.matches) submit(); });
      });
    }

    if (sort && !sort.dataset.ready) {
      sort.dataset.ready = 'true';
      sort.addEventListener('change', () => {
        const mirror = form && $('[data-sort-mirror]', form);
        if (mirror) {
          mirror.value = sort.value;
          submit();
        } else {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sort.value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
        }
      });
    }
  }

  /* ---------- Empfehlungen ---------- */
  function initRecommendations(ctx = document) {
    $$('[data-recommendations]', ctx).forEach(async (el) => {
      if (el.dataset.ready || !el.dataset.url) return;
      el.dataset.ready = 'true';
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initSliders(el);
        } else {
          el.remove();
        }
      } catch (e) { /* ignore */ }
    });
  }

  /* ---------- Kleinigkeiten ---------- */
  // Kollektionsbeschreibung: „Weiterlesen“ nur zeigen, wenn der Text auf dem Handy wirklich gekürzt ist
  function initReadMore(ctx = document) {
    $$('[data-read-more-toggle]', ctx).forEach((btn) => {
      if (btn.dataset.ready) return;
      btn.dataset.ready = 'true';
      const text = document.getElementById(btn.getAttribute('aria-controls'));
      if (!text) return;
      const check = () => btn.classList.toggle('is-needed', !text.classList.contains('is-clamped') || text.scrollHeight > text.clientHeight + 2);
      check();
      window.addEventListener('resize', debounce(check, 150));
      btn.addEventListener('click', () => {
        const open = text.classList.toggle('is-clamped') === false;
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      });
    });
  }
  function initPrefill() {
    const watch = new URLSearchParams(window.location.search).get('watch');
    if (!watch) return;
    $$('[data-prefill-watch]').forEach((input) => { input.value = watch; });
    const form = $('#ContactForm');
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function initRecover() {
    const panels = { login: $('[data-login-panel]'), recover: $('[data-recover-panel]') };
    if (!panels.login || !panels.recover) return;
    const toggle = (showRecover) => {
      panels.login.hidden = showRecover;
      panels.recover.hidden = !showRecover;
    };
    $$('[data-toggle-recover]').forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault();
      toggle(panels.recover.hidden);
    }));
    if (window.location.hash === '#recover' || panels.recover.querySelector('.form-message')) toggle(true);
  }

  function initReveal(ctx = document) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (!('IntersectionObserver' in window)) return;
    const targets = $$('.section-heading, .tile, .service, .step, .brand-tile, .iwt__media, .iwt__content, .article-card', ctx)
      .filter((el) => !el.classList.contains('reveal'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach((el, i) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('reveal');
      el.style.transitionDelay = `${(i % 4) * 80}ms`;
      io.observe(el);
    });
  }

  /* ---------- Land / Sprache ---------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-localization-select]');
    if (select && select.form) select.form.submit();
  });

  /* ---------- Globale Events ---------- */
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) { e.preventDefault(); openDrawer(openBtn.dataset.drawerOpen); return; }

    const cartBtn = e.target.closest('[data-cart-open]');
    if (cartBtn && $('#CartDrawer')) { e.preventDefault(); openDrawer('cart'); return; }

    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-backdrop]')) { closeAll(); return; }

    const modalBtn = e.target.closest('[data-modal-open]');
    if (modalBtn) {
      // Fenster in den Body verschieben, damit es nicht die Farben der Section (z. B. Espresso) erbt
      const id = modalBtn.dataset.modalOpen;
      const section = modalBtn.closest('.shopify-section');
      const dialog = (section && section.querySelector(`[id="${id}"]`)) || document.getElementById(id);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      e.preventDefault();
      if (dialog.parentElement !== document.body) {
        $$(`body > dialog[id="${id}"]`).forEach((d) => d.remove());
        document.body.appendChild(dialog);
      }
      dialog.showModal();
      return;
    }
    const modal = e.target.closest('[data-modal]');
    if (modal && (e.target === modal || e.target.closest('[data-modal-close]'))) { modal.close(); return; }

    if (e.target.closest('[data-cookie-preferences]')) {
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') window.privacyBanner.showPreferences();
      return;
    }
    $$('[data-footer-policies][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });

    if (e.target.closest('[data-search-open]')) { e.preventDefault(); openSearch(); return; }
    if (e.target.closest('[data-search-close]')) { closeSearch(); return; }

    const change = e.target.closest('[data-cart-change]');
    if (change) {
      e.preventDefault();
      changeCartLine(parseInt(change.dataset.cartChange, 10), parseInt(change.dataset.qty, 10));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAll();
      $$('[data-footer-policies][open]').forEach((d) => d.removeAttribute('open'));
    }
  });

  function init(ctx = document) {
    initSlideshows(ctx);
    initReadMore(ctx);
    initSliders(ctx);
    initGallery(ctx);
    initVariants(ctx);
    initProductForms(ctx);
    initFacets(ctx);
    initRecommendations(ctx);
    initReveal(ctx);
    initMaps(ctx);
  }

  document.addEventListener('DOMContentLoaded', () => {
    moveOverlaysToBody();
    initHeader();
    initAnnouncement();
    initPredictiveSearch();
    initPrefill();
    initRecover();
    init();
  });

  /* ---------- Cookie-Einstellungen ---------- */
  // Eigener Button nur, wenn das Shopify-Banner aktiv ist und Shopify nicht selbst schon einen Cookie-Link einfügt
  function checkCookieLink() {
    const hasBanner = window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function';
    // Fügt Shopify selbst einen Cookie-Link in die Liste ein, wird der eigene ausgeblendet (sonst steht er doppelt da)
    // Nur ein echter Schalter zählt (Button oder Link ohne Zieladresse), kein Textlink etwa zu einer Cookie-Richtlinie
    const isSwitch = (el) => el.tagName === 'BUTTON' || !el.getAttribute('href') || /^(#|javascript:)/.test(el.getAttribute('href'));
    const foreign = $$('.footer__policies-list a, .footer__policies-list button').some((el) => !el.matches('[data-cookie-preferences]') && /cookie/i.test(el.textContent) && isSwitch(el));
    $$('[data-cookie-preferences]').forEach((btn) => { const li = btn.closest('li'); if (li) li.hidden = !hasBanner || foreign; });
  }
  window.addEventListener('load', () => {
    setTimeout(checkCookieLink, 1500);
    setTimeout(checkCookieLink, 4000);
  });
  $$('.footer__policies-list').forEach((list) => new MutationObserver(checkCookieLink).observe(list, { childList: true, subtree: true }));

  /* ---------- Google Maps (Zwei-Klick, ohne dauerhafte Freigabe) ---------- */
  function loadMap(box) {
    const tpl = box.querySelector('[data-map-template]');
    if (!tpl) return;
    box.replaceChildren(tpl.content.cloneNode(true));
    box.classList.add('is-loaded');
  }
  function initMaps() {
    // Früher gespeicherte Dauer-Freigabe entfernen
    try { localStorage.removeItem('wam-maps-consent'); } catch (e) { /* Speicher gesperrt */ }
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-map-load]');
    if (!btn) return;
    const box = btn.closest('[data-map-consent]');
    if (box) loadMap(box);
  });

  /* ---------- Gewährleistung im Warenkorb: Hinweis zeigen, wenn der Haken fehlt ---------- */
  document.addEventListener('invalid', (e) => {
    const box = e.target.closest && e.target.closest('[data-consent]');
    if (!box) return;
    e.preventDefault();
    box.classList.add('is-invalid');
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, true);
  document.addEventListener('change', (e) => {
    const box = e.target.closest && e.target.closest('[data-consent]');
    if (!box || !e.target.matches('[data-consent-check]')) return;
    if (e.target.checked) box.classList.remove('is-invalid');
    // Zeitpunkt der Zustimmung als Nachweis in der Bestellung (Ortszeit des Kunden mit Zeitzone)
    const time = $('[data-consent-time]', box);
    if (time) time.value = e.target.checked ? new Date().toLocaleString('de-DE', { timeZoneName: 'short' }) : '';
  });

  /* ---------- Theme-Editor ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    moveOverlaysToBody();
    initHeader();
    initAnnouncement();
    initPredictiveSearch();
    init(e.target);
  });
})();
